import React, { useEffect, useState, useRef } from 'react';
import { getFlights, createFlight, updateFlightStatus, deleteFlight, getShipments } from '../../api';

export default function Flights() {
  const [flights, setFlights] = useState([]);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [shipmentsByFlight, setShipmentsByFlight] = useState({});
  const [flownSearch, setFlownSearch] = useState('');
  const [selectedFlownId, setSelectedFlownId] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  const loadFlights = () => getFlights().then(setFlights).catch(console.error);

  useEffect(() => { loadFlights(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createFlight({ name: newName.trim() });
      setNewName('');
      loadFlights();
    } finally { setCreating(false); }
  };

  const handleStatus = async (id, status) => {
    await updateFlightStatus(id, status);
    setExpandedId(null);
    loadFlights();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this flight? Linked shipments will be unassigned.')) return;
    await deleteFlight(id);
    if (expandedId === id) setExpandedId(null);
    loadFlights();
  };

  const toggleExpand = async (flight) => {
    if (expandedId === flight.id) { setExpandedId(null); return; }
    setExpandedId(flight.id);
    const inFlight = await getShipments({ flight_id: flight.id });
    setShipmentsByFlight(prev => ({ ...prev, [flight.id]: inFlight }));
  };

  const pendingFlights = flights.filter(f => f.status === 'pending');
  const flownFlights = flights.filter(f => f.status === 'flown');
  const filteredFlown = flownSearch.trim()
    ? flownFlights.filter(f => f.name.toLowerCase().includes(flownSearch.toLowerCase()))
    : flownFlights;
  const selectedFlown = selectedFlownId ? flownFlights.find(f => f.id === selectedFlownId) ?? null : null;

  const renderFlightCard = (f) => {
    const isExpanded = expandedId === f.id;
    const inFlightShipments = shipmentsByFlight[f.id] || [];
    const isPending = f.status === 'pending';

    return (
      <div key={f.id} style={{ marginBottom: 8 }}>
        {/* Card header */}
        <div
          onClick={() => toggleExpand(f)}
          style={{
            background: 'var(--bg-tertiary)',
            border: `1px solid ${isExpanded ? 'rgba(94,161,255,0.3)' : 'var(--border)'}`,
            borderRadius: isExpanded ? '10px 10px 0 0' : 10,
            padding: '11px 14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            transition: 'all 0.15s',
          }}
        >
          <span style={{
            fontSize: 10, color: 'var(--text-muted)',
            display: 'inline-block',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(90deg)' : 'none',
          }}>▶</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{f.name}</div>
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 6 }}>{f.shipment_count} shipments</span>
          <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
            {isPending
              ? <button onClick={() => handleStatus(f.id, 'flown')} style={s.actionBtn}>✈ Flown</button>
              : <button onClick={() => handleStatus(f.id, 'pending')} style={s.actionBtn}>↩ Undo</button>
            }
            <button onClick={() => handleDelete(f.id)} style={s.deleteBtn}>Delete</button>
          </div>
        </div>

        {/* Expanded body */}
        {isExpanded && (
          <div style={{
            background: 'var(--bg-tertiary)',
            border: '1px solid rgba(94,161,255,0.3)',
            borderTop: '1px solid rgba(15,23,42,0.06)',
            borderRadius: '0 0 10px 10px',
            overflow: 'hidden',
          }}>
            {/* Shipments in flight */}
            <div style={s.subHeader}>
              SHIPMENTS IN THIS FLIGHT ({inFlightShipments.length})
            </div>
            {inFlightShipments.length === 0 ? (
              <div style={s.empty}>No shipments in this flight yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  {['Tracking', 'Recipient', 'Customer', 'KG'].map(h => <th key={h} style={s.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {inFlightShipments.map(sh => (
                    <tr key={sh.id} className="ship-row" style={{ borderBottom: '1px solid rgba(15,23,42,0.05)' }}>
                      <td style={{ ...s.td, fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--accent)', fontSize: 12 }}>{sh.tracking_code}</td>
                      <td style={s.td}>{sh.receiver_name}</td>
                      <td style={s.td}>{sh.customer_code ? <span style={s.codeBadge}>{sh.customer_code}</span> : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}</td>
                      <td style={{ ...s.td, fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: 12 }}>{sh.weight_kg || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <style>{`
        .fi:focus { outline:none; border-color:var(--accent)!important; box-shadow:0 0 0 3px var(--accent-bg)!important; }
        .fb:hover:not(:disabled) { background:var(--bg-elevated)!important; border-color:var(--accent)!important; }
        .ship-row:hover td { background: rgba(15,23,42,0.025); }
        .dd-item:hover { background: rgba(15,23,42,0.05)!important; cursor:pointer; }
      `}</style>

      {/* Topbar */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div className="page-eyebrow">MANAGEMENT</div>
          <h1 className="page-title">Flights</h1>
        </div>
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8 }}>
          <input className="fi" style={{ ...s.input, width: 180 }} value={newName}
            onChange={e => setNewName(e.target.value)} placeholder="e.g. YVR-APR07" required />
          <button className="fb" style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}
            type="submit" disabled={creating}>+ Create</button>
        </form>
      </div>

      {/* ── Section: Pending ── */}
      <div style={s.sectionHeader}>
        <span style={s.sectionTitle}>⏳ Pending</span>
        <span style={s.badge}>{pendingFlights.length}</span>
      </div>
      {pendingFlights.length === 0
        ? <div style={{ ...s.emptySection, marginBottom: 32 }}>No pending flights</div>
        : <div style={{ marginBottom: 32 }}>{pendingFlights.map(renderFlightCard)}</div>
      }

      {/* ── Section: Flown ── */}
      <div style={s.sectionHeader}>
        <span style={s.sectionTitle}>✈ Flown</span>
        <span style={s.badge}>{flownFlights.length}</span>
      </div>

      {/* Search box */}
      <div ref={searchRef} style={{ position: 'relative', marginBottom: 14 }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--text-muted)', pointerEvents: 'none' }}>🔍</span>
          <input
            className="fi"
            style={{ ...s.input, width: '100%', paddingLeft: 34, boxSizing: 'border-box' }}
            value={flownSearch}
            onChange={e => { setFlownSearch(e.target.value); setSelectedFlownId(null); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            placeholder="Type flight name or pick from list..."
          />
          {flownSearch && (
            <button onClick={() => { setFlownSearch(''); setSelectedFlownId(null); }}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1 }}>
              ×
            </button>
          )}
        </div>

        {/* Dropdown */}
        {showDropdown && flownFlights.length > 0 && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            background: 'var(--bg-secondary)', border: '1px solid var(--border-strong)',
            borderRadius: 8, zIndex: 200, overflow: 'hidden',
            maxHeight: 220, overflowY: 'auto',
            boxShadow: 'var(--shadow-md)',
          }}>
            {(flownSearch.trim() ? filteredFlown : flownFlights).map(f => (
              <div key={f.id} className="dd-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setFlownSearch(f.name);
                  setSelectedFlownId(f.id);
                  setShowDropdown(false);
                }}
                style={{ padding: '9px 14px', fontSize: 13, color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(15,23,42,0.05)' }}
              >
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{f.name}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{f.shipment_count} shipments</span>
              </div>
            ))}
            {flownSearch.trim() && filteredFlown.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>Not found</div>
            )}
          </div>
        )}
      </div>

      {flownFlights.length === 0
        ? <div style={s.emptySection}>No completed flights yet</div>
        : selectedFlown
          ? renderFlightCard(selectedFlown)
          : null
      }
    </div>
  );
}

const s = {
  input: { background: 'var(--bg-tertiary)', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: 'var(--text-primary)', transition: 'all 0.18s', outline: 'none' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid rgba(15,23,42,0.07)' },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.8 },
  badge: { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--border)', border: '1px solid rgba(15,23,42,0.10)', padding: '2px 8px', borderRadius: 20 },
  emptySection: { padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 },
  empty: { padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 },
  subHeader: { padding: '10px 14px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 1.2, textTransform: 'uppercase', borderBottom: '1px solid rgba(15,23,42,0.07)' },
  th: { background: 'var(--bg-tertiary)', padding: '9px 12px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, textAlign: 'left', borderBottom: '1px solid rgba(15,23,42,0.07)' },
  td: { padding: '10px 12px', fontSize: 13, color: 'var(--text-primary)' },
  codeBadge: { fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 12, color: 'var(--accent)', background: 'var(--accent-bg)', border: '1px solid rgba(94,161,255,0.2)', padding: '2px 7px', borderRadius: 5 },
  actionBtn: { background: 'var(--border)', border: '1px solid rgba(15,23,42,0.10)', color: 'var(--text-secondary)', borderRadius: 5, padding: '4px 9px', fontSize: 11, cursor: 'pointer', transition: 'all 0.15s' },
  deleteBtn: { background: 'transparent', border: '1px solid rgba(220,38,38,0.20)', color: '#b91c1c', borderRadius: 5, padding: '4px 9px', fontSize: 11, cursor: 'pointer', transition: 'all 0.15s' },
};
